/* eslint-disable camelcase */
/* eslint-disable no-case-declarations */
import React from 'react'
import { Schema, TableData, TableKey, Customizations, Visibility, IntervalDTO } from 'models/schema'
// import { useGetSchema } from 'utils/hooks/queries/useGetSchema'
import { compareObjectsDifferent } from 'utils/helpers'
import { defaultIntervals, defaultSchema, defaultSchemaAndIntervals } from 'utils/constants'
import { useGetSchema } from 'utils/hooks/queries/useGetSchema'

type SchemaReducerState = {
  schema: Schema
  order: Array<string>
  currentTableKey: TableKey
  intervals: Array<IntervalDTO>
}

const defaultSchemaReducerState: SchemaReducerState = {
  schema: defaultSchemaAndIntervals.schema,
  order: Object.keys(
    defaultSchemaAndIntervals.schema.tableMappings.find(
      (mapping) => mapping.tableName === 'futureSales',
    )?.customizations as Customizations,
  ),
  currentTableKey: 'futureSales',
  intervals: defaultIntervals,
}

const schemaActions = {
  SET_STATE: 'SET_STATE',
  SET_CURRENT_TABLE_KEY: 'SET_CURRENT_TABLE_KEY',
  SET_ORDER: 'SET_ORDER',
  SET_SCHEMA: 'SET_SCHEMA',
} as const

type SchemaActionsKeys = keyof typeof schemaActions
type SchemaActions = (typeof schemaActions)[SchemaActionsKeys]

type SchemaReducerAction = {
  type: SchemaActions
  property?: keyof Customizations
  payload?: unknown
}

const reducer = (state: SchemaReducerState, action: SchemaReducerAction): SchemaReducerState => {
  switch (action.type) {
    case schemaActions.SET_STATE: {
      return { ...state, ...(action.payload as unknown as object) }
    }

    case schemaActions.SET_SCHEMA:
      return { ...state, schema: action.payload as unknown as Schema }

    case schemaActions.SET_ORDER:
      return { ...state, order: action.payload as unknown as Array<string> }

    case schemaActions.SET_CURRENT_TABLE_KEY:
      const figureOutWhichPanel: TableKey = (() => {
        const loc = window.location.pathname
        if (loc.includes('future-sales'))
          return loc.endsWith('future-sales') ? 'futureSales' : 'futureSalesDetail'
        if (loc.includes('sales-result'))
          return loc.endsWith('sales-result') ? 'salesResult' : 'salesResultDetail'
        if (loc === '/waiting-files') return 'waitingFiles'
        if (loc === '/stock') return 'stock'
        if (loc === '/history') return 'history'
        return 'search'
      })()

      return figureOutWhichPanel === state.currentTableKey
        ? state
        : {
            ...state,
            currentTableKey: figureOutWhichPanel,
          }

    default:
      return state
  }
}

type SchemaStateContextProps = SchemaReducerState & {
  isLoading: boolean
  isError: boolean
}

const defaultSchemaStateContext: SchemaStateContextProps = {
  schema: defaultSchema,
  order: Object.keys(
    defaultSchema.tableMappings.find((cust) => cust.tableName === 'futureSales')
      ?.customizations as Array<string>,
  ),
  currentTableKey: 'futureSales',
  intervals: defaultIntervals,
  isLoading: false,
  isError: false,
}
type SchemaActionContextProps = {
  setCurrentTableKey: () => void
  setSchema: (newShema: Schema) => void
  setIntervals: (newIntervals: Array<IntervalDTO>) => void
  setOrder: (newOrder: Array<string>) => void
}

const getCustomizationKeys = (customizations: Customizations) => Object.keys(customizations)

const reconcileCustomizations = (
  defaultCustomizations: Customizations,
  fetchedCustomizations?: Customizations,
): Customizations => {
  const defaultKeys = getCustomizationKeys(defaultCustomizations)
  const fetchedKeys = getCustomizationKeys(fetchedCustomizations || {}).filter(
    (key) => key in defaultCustomizations,
  )
  const orderedKeys = [
    ...fetchedKeys,
    ...defaultKeys.filter((key) => !fetchedKeys.includes(key)),
  ] as Array<keyof Customizations>

  return orderedKeys.reduce<Customizations>((acc, key) => {
    const defaultCustomization = defaultCustomizations[key]
    const fetchedCustomization = fetchedCustomizations?.[key]

    if (!defaultCustomization) {
      return acc
    }

    acc[key] = {
      label: defaultCustomization.label,
      isDisplayed: fetchedCustomization?.isDisplayed ?? defaultCustomization.isDisplayed,
    }
    return acc
  }, {})
}

const reconcileTableMappings = (schema: Schema): Schema['tableMappings'] =>
  defaultSchemaAndIntervals.schema.tableMappings.map((defaultMapping) => {
    const fetchedMapping = schema.tableMappings.find(
      (mapping) => mapping.tableName === defaultMapping.tableName,
    )

    return {
      tableName: defaultMapping.tableName,
      customizations: reconcileCustomizations(
        defaultMapping.customizations,
        fetchedMapping?.customizations,
      ),
    }
  })

const defaultSchemaActionContext: SchemaActionContextProps = {
  setCurrentTableKey: () => {},
  setSchema: (_newShema: Schema) => {},
  setIntervals: (_newIntervals: Array<IntervalDTO>) => {},
  setOrder: (_newOrder: Array<string>) => {},
}

const SchemaStateContext = React.createContext<SchemaStateContextProps>(defaultSchemaStateContext)
const SchemaActionContext = React.createContext<SchemaActionContextProps>(
  defaultSchemaActionContext,
)

const SchemaProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = React.useReducer(reducer, defaultSchemaReducerState)
  const { schema, currentTableKey, intervals, order } = state
  const currentCustomization = React.useMemo(
    () =>
      schema.tableMappings.find((mapping) => mapping.tableName === currentTableKey)
        ?.customizations as Customizations,
    [currentTableKey, schema.tableMappings],
  )

  const actions = React.useMemo(
    () => ({
      setIntervals: (newIntervals: Array<IntervalDTO>) => {
        dispatch({ type: schemaActions.SET_STATE, payload: { intervals: newIntervals } })
      },

      setSchema: (newShema: Schema) => {
        dispatch({ type: schemaActions.SET_SCHEMA, payload: newShema })
      },
      setCurrentTableKey: () => {
        dispatch({ type: schemaActions.SET_CURRENT_TABLE_KEY })
      },
      setOrder: (newOrder: Array<string>) => {
        dispatch({ type: schemaActions.SET_ORDER, payload: newOrder })
      },
    }),
    [],
  )

  const { data: fetchedSchemaAndIntervals, isLoading, isError } = useGetSchema()

  React.useEffect(() => {
    if (!fetchedSchemaAndIntervals) {
      return
    }
    const fetchedMappings = fetchedSchemaAndIntervals?.schema?.tableMappings ?? []
    const fetchedIntervals = fetchedSchemaAndIntervals?.intervals ?? []

    const standardizedPersonnnalSchema = {
      ...fetchedSchemaAndIntervals.schema,
      tableMappings: reconcileTableMappings({
        ...fetchedSchemaAndIntervals.schema,
        tableMappings: fetchedMappings,
      }),
    }
    const standardizedPersonnnalIntervals = defaultIntervals.map((defaultInterval) => {
      return (
        fetchedIntervals.find(
          (fetchedInterval) => fetchedInterval.tableName === defaultInterval.tableName,
        ) || defaultInterval
      )
    })
    const updatedOrder = Object.keys(
      standardizedPersonnnalSchema.tableMappings.find(
        (mapping) => mapping.tableName === currentTableKey,
      )?.customizations as Customizations,
    )
    const payloadState = {
      schema: standardizedPersonnnalSchema,
      intervals: standardizedPersonnnalIntervals,
      order: updatedOrder,
    }
    dispatch({
      type: schemaActions.SET_STATE,
      payload: payloadState,
    })
  }, [currentTableKey, fetchedSchemaAndIntervals])

  React.useEffect(() => {
    const currentOrder = Object.keys(
      schema.tableMappings.find((mapping) => mapping.tableName === currentTableKey)
        ?.customizations as Customizations,
    )
    dispatch({
      type: 'SET_ORDER',
      payload: currentOrder,
    })
  }, [currentTableKey, schema.tableMappings])

  React.useEffect(() => {
    const customizationKeys = Object.keys(currentCustomization)
    const doesOrderMatchCurrentTable =
      order.length === customizationKeys.length &&
      order.every((value) => Object.prototype.hasOwnProperty.call(currentCustomization, value))

    if (!doesOrderMatchCurrentTable) {
      return
    }

    const newCustomization = order.reduce((acc, value) => {
      return { ...acc, [value]: currentCustomization[value as keyof typeof currentCustomization] }
    }, {})

    if (compareObjectsDifferent(newCustomization, currentCustomization)) {
      dispatch({
        type: 'SET_SCHEMA',
        payload: {
          ...schema,
          tableMappings: schema.tableMappings.map((mapping) => {
            return mapping.tableName === currentTableKey
              ? { ...mapping, customizations: newCustomization }
              : mapping
          }),
        },
      })
    }
  }, [currentCustomization, currentTableKey, order, schema, schema.tableMappings])

  const stateProviderValue = React.useMemo<SchemaStateContextProps>(
    () => ({
      schema,
      order,
      intervals,
      currentTableKey,
      isLoading,
      isError,
    }),
    [schema, currentTableKey, isLoading, isError, order, intervals],
  )
  return (
    <SchemaStateContext.Provider value={stateProviderValue}>
      <SchemaActionContext.Provider value={actions}>{children}</SchemaActionContext.Provider>
    </SchemaStateContext.Provider>
  )
}

const useSchemaState = () => React.useContext(SchemaStateContext)
const useSchemaAction = () => React.useContext(SchemaActionContext)

export { SchemaProvider, useSchemaState, useSchemaAction }
export type { Visibility, TableData }
